import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { ShareModel, Contact, ContactModel, GroupModel } from "common247";
import { ThemedPopupScreen } from "../ThemedPopupScreen";
import { SharingControl } from "./SharingControl";
import { ThemedButton } from "../ThemedButton";
import { type IPopoverAnchor } from "@/utils/popover-positioning";
import { useThemeColor } from "@/hooks/useThemeColor";

/**
 * Props for the ShareSelector component.
 * Uses the Popup UX pattern to let users manage and select one or many shares
 * for the entity currently on screen.
 */
export interface IShareSelectorProps {
  /** Whether the selector popup modal is visible */
  visible: boolean;
  /** Callback fired to close the selector popup */
  onClose: () => void;
  /** Optional custom title (default: "Manage Sharing") */
  title?: string;
  /** Title or name of the item being shared (e.g. Memory title, Contact name, Group name) */
  itemTitle?: string;
  /** The entity domain type being shared */
  itemType?: "memory" | "media" | "contact" | "group";
  /** Current shares associated with the item */
  shares: ShareModel[];
  /** Available contacts list for adding new shares */
  contacts?: (Contact | ContactModel)[];
  /** Available groups list for adding new shares */
  groups?: GroupModel[];
  /** Whether sharing data is actively loading */
  isLoading?: boolean;
  /** Whether the current user is permitted to create new shares */
  canShare?: boolean;
  /** Whether the current user is permitted to revoke existing shares */
  canUnshare?: boolean;
  /** Read-only mode override */
  readOnly?: boolean;
  /** Optional contextual coordinates of trigger element (for popup placement) */
  anchorPosition?: IPopoverAnchor | null;
  /** Callback invoked when a user grants access to a contact or group */
  onShare: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  /** Callback invoked when a user revokes access for a share */
  onUnshare: (shareId: string) => void;
  /** Optional testID for testing */
  testID?: string;
}



/**
 * Standardized Contextual Share Selector.
 * Uses the Popup UX pattern (ThemedPopupScreen) to allow the user to select one or many
 * contacts or groups to share an item with, and manage existing entity shares.
 * All selectors in the platform adhere to this popup UX pattern.
 */
export function ShareSelector({
  visible,
  onClose,
  title = "Manage Sharing",
  itemTitle,
  itemType,
  shares = [],
  contacts = [],
  groups = [],
  isLoading = false,
  canShare = true,
  canUnshare = true,
  readOnly = false,
  anchorPosition,
  onShare,
  onUnshare,
  testID,
}: IShareSelectorProps): React.JSX.Element | null {
  if (!visible) return null;

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");

  const typePrefix = itemType ? `${itemType.toUpperCase()}: ` : "";
  const subtitle = itemTitle ? `${typePrefix}${itemTitle}` : undefined;

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={title}
      icon="share-social-outline"
      subtitle={subtitle}
      width={440}
      maxHeight={500}
      cardStyle={{ backgroundColor: cardBg, borderColor }}
      testID={testID || "share-selector"}
      footer={
        <View style={styles.footerContainer}>
          <ThemedButton
            title="Done"
            variant="secondary"
            onPress={onClose}
            compact
            testID={testID ? `${testID}-done-btn` : "share-selector-done-btn"}
            style={styles.doneBtn}
          />
        </View>
      }
    >
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
        keyboardShouldPersistTaps="handled"
      >
        <SharingControl
          shares={shares}
          contacts={contacts}
          groups={groups}
          isLoading={isLoading}
          canShare={canShare}
          canUnshare={canUnshare}
          readOnly={readOnly}
          onShare={onShare}
          onUnshare={onUnshare}
        />
      </ScrollView>
    </ThemedPopupScreen>
  );
}



export default ShareSelector;

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
  footerContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingTop: 8,
  },
  doneBtn: {
    minWidth: 84,
  },
});
